import React, { useState, useEffect, ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import {
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useListing, useLanguage } from 'hooks';
import moment from 'moment';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ENG, VIRTUAL_SERVICE_OFFER_TYPE } from 'config/constants';
import { VirtualServiceOrderItem, Picture } from 'models';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    '@media (min-width: 768px)': {
      minWidth: 550,
    },
  },
  picture: {
    width: 200,
  },
  items: {
    overflowY: 'scroll',
    maxHeight: 500,
  },
  code: {
    width: '100%',
    marginRight: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    alignItems: 'center',
  },
  priceColumn: {
    marginLeft: 200,
  },
  total: {
    fontWeight: 600,
  },
  summary: {
    fontSize: 12,
  },
  accordion: {
    backgroundColor: '#f0f0f0',
  },
  accordionDetails: {
    position: 'relative',
    width: 'auto',
  },
  newOrderButton: {
    marginTop: 20,
  },
  addIcon: {
    marginRight: 5,
  },
  optionTitle: {
    fontSize: 10,
  },
  optionSubtitle: {
    fontSize: 10,
  },
});
interface Props {
  setDisplayPrevOrders: (_: boolean) => void;
}
function PreviousOrders({ setDisplayPrevOrders }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();

  const path = window.location.pathname;
  const isEnglish = language === ENG;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing, virtualServiceOrders } = useListing(listing_token);

  const [currentOrderId, setCurrentOrderId] = useState(
    virtualServiceOrders ? virtualServiceOrders[0] : 0,
  );
  const [currentOrderExpanded, setCurrentOrderExpanded] = useState(false);

  const fmtServiceName = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.serviceName',
  });

  const getOptionTitle = (item: VirtualServiceOrderItem) => {
    if (!item.code) {
      if (item.note) {
        return `${intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.autoSelect' })} (${
          item.note
        })`;
      }
      return intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.autoSelect' });
    }
    return item.code;
  };

  const onExpand = (id: number) => {
    setCurrentOrderId(id);
    if (currentOrderId === id) {
      setCurrentOrderExpanded(!currentOrderExpanded);
    } else {
      setCurrentOrderExpanded(true);
    }
  };

  const getImageUrl = (item: any) => {
    const foundPicture = listing.pictures.find((pic: Picture) => {
      return Number(pic.id) === item.picture_id;
    });
    if (foundPicture) {
      return foundPicture.thumbnail_url;
    }
    return null;
  };

  const getOrderSubtotal = (virtualServiceOrder: any) => {
    const quantity = virtualServiceOrder.items.length;
    const subtotal = virtualServiceOrder.service_offer.price * quantity;
    return subtotal.toFixed(2);
  };

  const getOrderTaxes = (virtualServiceOrder: any) => {
    const quantity = virtualServiceOrder.items.length;
    return (virtualServiceOrder.service_offer.price * quantity * 0.14975).toFixed(2);
  };

  const getOrderTotal = (virtualServiceOrder: any) => {
    const quantity = virtualServiceOrder.items.length;
    return (virtualServiceOrder.service_offer.price * quantity * 1.14975).toFixed(2);
  };

  return (
    <div className={styles.content}>
      <div className={styles.items}>
        {virtualServiceOrders &&
          virtualServiceOrders.map((virtualServiceOrder: any, index) => {
            return (
              <Accordion
                key={index}
                className={styles.accordion}
                expanded={currentOrderId === virtualServiceOrder.id && currentOrderExpanded}
                onChange={() => {
                  return onExpand(virtualServiceOrder.id);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <div>
                    <Typography className={styles.summary}>
                      {intl.formatMessage({
                        id: 'deliver.photos.virtualServiceModal.order',
                      })}
                      {virtualServiceOrder.id}
                    </Typography>
                    <Typography className={styles.summary}>
                      {fmtServiceName}
                      {isEnglish
                        ? virtualServiceOrder.service_offer.display_name
                        : virtualServiceOrder.service_offer.display_name_Fr}
                    </Typography>
                    <Typography className={styles.summary}>
                      {intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.date' })}
                      {moment
                        .utc(virtualServiceOrder.created_at)
                        .local()
                        .format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                  </div>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography>
                              {intl.formatMessage({
                                id: 'deliver.photos.virtualServiceModal.photos',
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>
                              {intl.formatMessage({
                                id: 'deliver.photos.virtualServiceModal.price',
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {virtualServiceOrder.items.map(
                          (item: VirtualServiceOrderItem, index: number) => {
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <div>
                                    <img
                                      className={styles.picture}
                                      src={getImageUrl(item)}
                                      alt='item'
                                    />
                                    {virtualServiceOrder.order_type !==
                                      VIRTUAL_SERVICE_OFFER_TYPE.VIRTUAL_BLUE_HOUR && (
                                      <div>
                                        <Typography className={styles.optionTitle}>
                                          {intl.formatMessage({
                                            id: 'deliver.photos.virtualServiceModal.yourChoice',
                                          })}
                                        </Typography>
                                        <Typography className={styles.optionSubtitle}>
                                          {getOptionTitle(item)}
                                        </Typography>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell align='right'>
                                  <Typography>{`$${virtualServiceOrder.service_offer.price}`}</Typography>
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                        <TableRow>
                          <TableCell>
                            <Typography>
                              {intl.formatMessage({
                                id: 'deliver.photos.virtualServiceModal.subtotal',
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$${getOrderSubtotal(virtualServiceOrder)}`}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Typography>
                              {intl.formatMessage({
                                id: 'deliver.photos.virtualServiceModal.taxes',
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$${getOrderTaxes(virtualServiceOrder)}`}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Typography>
                              {intl.formatMessage({
                                id: 'deliver.photos.virtualServiceModal.total',
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$${getOrderTotal(virtualServiceOrder)}`}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </div>
    </div>
  );
}
export default PreviousOrders;
